import { isValidElement, useCallback, useMemo } from 'react';
import { useOptionalLanguage } from '../../context/LanguageContext';
import { translateUiText, formatUiText } from './centralLocalization';

type Language = 'en' | 'ar';
export function translateCrmText(text: string, language: Language): string {
  return translateUiText(text, language, 'crm');
}
function formatText(template: string, values: readonly unknown[], language: Language) {
  return formatUiText(template, values, language, 'crm');
}
const metadata = new Set(['label', 'title', 'header', 'hint', 'caption', 'emptyText', 'message', 'okText', 'cancelText']);
const dataKeys = new Set(['name', 'value', 'key', 'id', 'course', 'data', 'student', 'customer', 'row', 'lead', 'employee', 'notes', 'content', 'description']);
function translateLabels<T>(value: T, language: Language): T {
  if (!value || typeof value !== 'object' || isValidElement(value)) return value;
  if (!Array.isArray(value) && ![Object.prototype, null].includes(Object.getPrototypeOf(value))) return value;
  const preserve = (value as { translate?: boolean }).translate === false;
  const result = Array.isArray(value) ? [...value] : { ...value };
  for (const [key, item] of Object.entries(value)) {
    if (dataKeys.has(key) || (preserve && ['label', 'title'].includes(key))) continue;
    (result as Record<string, unknown>)[key] = metadata.has(key) && typeof item === 'string' ? translateCrmText(item, language) : translateLabels(item, language);
  }
  return result as T;
}
const currentLanguage = (): Language => typeof document !== 'undefined' && document.documentElement.lang === 'ar' ? 'ar' : 'en';
export const crmText = <T,>(value: T): T => (typeof value === 'string' ? translateCrmText(value, currentLanguage()) : value) as T;
export const crmFormat = (template: string, values: readonly unknown[]) => formatText(template, values, currentLanguage());
export const crmLabels = <T,>(value: T): T => translateLabels(value, currentLanguage());
export function useCrmLocale() {
  const language = useOptionalLanguage()?.language ?? 'en';
  const t = useCallback(<T,>(value: T): T => (typeof value === 'string' ? translateCrmText(value, language) : value) as T, [language]);
  const format = useCallback((template: string, values: readonly unknown[]) => formatText(template, values, language), [language]);
  const labels = useCallback(<T,>(value: T): T => translateLabels(value, language), [language]);
  return useMemo(() => ({ language, t, format, labels }), [language, t, format, labels]);
}
